import { Suspense, useEffect } from "react";
import { Switch, Route, Router } from "wouter";

// Custom location hook. The app is primarily hash-routed (so static hosts
// serve a single index.html for every URL), but a handful of routes are
// served as real paths so they can be deep-linked from email or admin
// dashboards — notably /portal/admin/* and /auth/*. When the current
// pathname matches one of those prefixes we read window.location.pathname;
// otherwise we read window.location.hash as before. main.tsx skips the
// "#/" hash injection for the same set of prefixes, which keeps the URL
// clean (no trailing "#/" on admin pages).
import { useSyncExternalStore } from "react";
import { hashSubscribe, hashGetSnapshot, hashNavigate } from "@/lib/hashRouter";
import { lazyRoute } from "@/lib/lazyRoute";
import { startSpriteWarm } from "@/lib/spriteWarm";

/** The longest the splash waits for the first screen's sprites to warm. */
const SPRITE_WARM_SPLASH_CAP_MS = 1200;
function useHashLocation(): [string, (to: string, opts?: { replace?: boolean }) => void] {
  const location = useSyncExternalStore(hashSubscribe, hashGetSnapshot);
  return [location, hashNavigate];
}
import { queryClient, apiRequestRaw, hasAuthToken } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { MyPokemonImportBar } from "@/components/MyPokemonImportBar";
import { AutoScanBar } from "@/components/AutoScanBar";
import { DexLinkUndoBar } from "@/components/DexLinkUndoBar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SessionProvider } from "@/components/SessionProvider";
import { InventoryProvider } from "@/components/InventoryProvider";
import { DexSpriteProvider } from "@/components/DexSpriteProvider";
import { BattleProvider } from "@/components/BattleProvider";
import { BroadcastProvider } from "@/components/BroadcastProvider";
import { LiveScanProvider } from "@/components/LiveScanProvider";
import { AuthProvider } from "@/components/AuthProvider";
import { AuthModal } from "@/components/AuthModal";
import { GlobalKeyboard } from "@/components/GlobalKeyboard";
import { ProfileGate } from "@/components/ProfileGate";
import { PageViewReporter } from "@/components/PageViewReporter";
import { HeardAboutPrompt } from "@/components/HeardAboutPrompt";
import { FeatureIdeaPrompt } from "@/components/FeatureIdeaPrompt";
import { LiveEventsIntroSheet } from "@/components/LiveEventsIntroSheet";
import { RatingMoments } from "@/components/RatingMoments";
import { SearchMovedNotice } from "@/components/SearchMovedNotice";
import { WhyProSheet } from "@/components/WhyProSheet";
import { ActiveBonusesOverlay } from "@/components/ActiveBonusesOverlay";
import { GlobalDexLiveScanMenu } from "@/components/GlobalDexLiveScanMenu";
import { LiveScanFloatingOrb } from "@/components/LiveScanFloatingOrb";
import { LiveScanHudActions } from "@/components/LiveScanHudActions";
import { RequireAuth } from "@/components/RequireAuth";
import { TradeExecutionFlow } from "@/components/TradeExecutionFlow";
import { FeedbackButton } from "@/components/FeedbackButton";
import { PrioritiesSyncer } from "@/components/PrioritiesSyncer";
import { WishlistSyncer } from "@/components/WishlistSyncer";
import { MyPokemonSyncer } from "@/components/MyPokemonSyncer";
import { AutoScanOnOpen } from "@/components/AutoScanOnOpen";
import { DexVisibilitySyncer } from "@/components/DexVisibilitySyncer";
import { ShadowRosterSyncer } from "@/components/ShadowRosterSyncer";
import { DexConfigSyncer } from "@/components/DexConfigSyncer";
import { CopyOverridesSyncer } from "@/components/CopyOverridesSyncer";
import { CopyEditMode } from "@/components/CopyEditMode";
import { PushLifecycle } from "@/components/PushLifecycle";
import { DexSyncOnSignIn } from "@/components/DexSyncOnSignIn";
import { GlobalErrorReporter } from "@/components/GlobalErrorReporter";
import { GuidedTour } from "@/components/GuidedTour";
import { DeepLinkHandler } from "@/components/DeepLinkHandler";
import NotFound from "@/pages/not-found";
import PortalHome, { PortalDex, PortalUpload, PortalAccount } from "@/pages/Portal";
// Admin pages are admin-only and not on any user critical path. Code-splitting
// them shaves them out of the initial JS bundle so testers and visitors load
// less on the first paint.
const AdminDataflow = lazyRoute(() => import("@/pages/AdminDataflow"));
const AdminWiring = lazyRoute(() => import("@/pages/AdminWiring"));
const AdminEmailInbox = lazyRoute(() => import("@/pages/AdminEmailInbox"));
const AdminActivity = lazyRoute(() => import("@/pages/AdminActivity"));
const AdminMyData = lazyRoute(() => import("@/pages/AdminMyData"));
const AdminCaptures = lazyRoute(() => import("@/pages/AdminCaptures"));
// One trainer, everything we hold about them. Accounts answers "who is on the
// platform"; this answers "tell me about this person".
const AdminUser = lazyRoute(() => import("@/pages/AdminUser"));
// The newest sign-ups with their first-session story: acquisition, first-48h
// page trail, onboarding milestones and whether they came back.
const AdminNewJoiners = lazyRoute(() => import("@/pages/AdminNewJoiners"));
const AdminLoyal = lazyRoute(() => import("@/pages/AdminLoyal"));
const AdminNoticeWinback = lazyRoute(() => import("@/pages/AdminNoticeWinback"));
const AdminDevices = lazyRoute(() => import("@/pages/AdminDevices"));
// Backups + config as one tabbed page. The two standalone routes below still
// resolve, so existing links and bookmarks keep working.
const AdminDex = lazyRoute(() => import("@/pages/AdminDex"));
const AdminBrowsing = lazyRoute(() => import("@/pages/AdminBrowsing"));
const AdminGrowth = lazyRoute(() => import("@/pages/AdminGrowth"));
const AdminAcquisition = lazyRoute(() => import("@/pages/AdminAcquisition"));
const AdminDexBackups = lazyRoute(() => import("@/pages/AdminDexBackups"));
const AdminReferrals = lazyRoute(() => import("@/pages/AdminReferrals"));
const AdminEmailCompose = lazyRoute(() => import("@/pages/AdminEmailCompose"));
const AdminDexConfig = lazyRoute(() => import("@/pages/AdminDexConfig"));
// The Game Master's trade rules, read-only. Standalone route alongside the Dex
// page's Trading tab, matching how Backups and Config each keep a direct link.
const AdminDexTradability = lazyRoute(() => import("@/pages/AdminDexTradability"));
const AdminEvents = lazyRoute(() => import("@/pages/AdminEvents"));
const AdminBetaSignups = lazyRoute(() => import("@/pages/AdminBetaSignups"));
const AdminPlayNotify = lazyRoute(() => import("@/pages/AdminPlayNotify"));
const AdminDeletions = lazyRoute(() => import("@/pages/AdminDeletions"));
// Reported raid hosts. Reachable now so the queue exists before raids launch;
// it is admin-gated like every other portal page.
const AdminRaidReports = lazyRoute(() => import("@/pages/AdminRaidReports"));
const AdminHome = lazyRoute(() => import("@/pages/AdminHome"));
// Reference: database backups (moved off Activity, 2026-10-15) and the list
// of archived tabs (campaigns that have ended; routes and data kept).
const AdminSystem = lazyRoute(() => import("@/pages/AdminSystem"));
const AdminArchive = lazyRoute(() => import("@/pages/AdminArchive"));
// v2 admin analytics dashboard (Portal). Charts + KPIs from the Worker's
// /api/admin/analytics/summary aggregate.
const AdminAnalyticsV2 = lazyRoute(() => import("@/pages/AdminAnalyticsV2"));
import AuthVerifyEmail from "@/pages/AuthVerifyEmail";
import AuthForgotPassword from "@/pages/AuthForgotPassword";
import AuthResetPassword from "@/pages/AuthResetPassword";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Profile from "@/pages/Profile";
import Friends from "@/pages/Friends";
// Lazy: the management page is a deliberate detour off the Friends tab, not
// something every session opens, so it stays out of the main bundle.
const FriendsManage = lazyRoute(() => import("@/pages/FriendsManage"));
import Compare from "@/pages/Compare";
import TradeHub from "@/pages/trades/TradeHub";
import Trade from "@/pages/Trade";
import CalendarPage from "@/pages/Calendar";
// Bonuses: which events give you what right now (event bonuses + GO Pass
// perks). Lazy: reached from the floating overlay pill / main menu.
const BonusesPage = lazyRoute(() => import("@/pages/Bonuses"));
// PR-F: Pokedex completion page lazy-loads pokedexOcr/journalOcr only when
// the user opens the scan modals.
const importPokedexPage = () => import("@/pages/Pokedex");
const PokedexPage = lazyRoute(importPokedexPage);
// wish-list-screen: "What are you Chasing?" — the trainer's up-to-seven wanted
// copies. Lazy-loaded (reached from the hero medallion long-press menu).
const WishListPage = lazyRoute(() => import("@/pages/WishList"));
// Per-species detail page (/pokemon/:dexNumber). Lazy-loaded so its variant
// resolver + sprite art don't ship in the initial bundle. Forms (Vivillon,
// Furfrou, Unown, Spinda, regional variants, etc.) now live in a collapsible
// section on this page rather than a standalone sub-dex route.
const PokemonDetailPage = lazyRoute(() => import("@/pages/Pokemon"));
// What each scan did to the dex, grouped by run, with an undo per entry. The
// per-individual-record detail page (/scan/:id) went with the scan-organizer
// model it belonged to — a scan changes cells, not creatures, and a cell's
// detail already has a home on the species page.
const ScanHistoryPage = lazyRoute(() => import("@/pages/ScanHistory"));
const WalkthroughsPage = lazyRoute(() => import("@/pages/Walkthroughs"));
// My Pokémon — the trainer's individual caught Pokémon (CP / IVs / moves),
// fed by the live scanner reading the in-game caught-Pokémon page. Lazy so
// the record store + filter engine stay out of the initial bundle.
const importMyPokemonPage = () => import("@/pages/MyPokemon");
const MyPokemonPage = lazyRoute(importMyPokemonPage);
const StardustPlannerPage = lazyRoute(() => import("@/pages/StardustPlanner"));
const MegaEvolutionsPage = lazyRoute(() => import("@/pages/MegaEvolutions"));
const MyPokemonDetailPage = lazyRoute(() => import("@/pages/MyPokemonDetail"));
// IV nickname studio: template setup for copy-ready GO names (2026-08-28).
const NamingPage = lazyRoute(() => import("@/pages/Naming"));
// Team GO Rocket: every line-up, your best three, and the dex rows this
// rotation's shadows could fill (2026-09-15).
const RocketPage = lazyRoute(() => import("@/pages/Rocket"));
const DynamaxPage = lazyRoute(() => import("@/pages/Dynamax"));
const ShowcasesPage = lazyRoute(() => import("@/pages/Showcases"));
// Box Cleanup advisor: review queue over the transfer classifier (2026-08-28).
const BoxCleanupPage = lazyRoute(() => import("@/pages/BoxCleanup"));
// Raid hosting. NOT LINKED: the routes work, nothing in the app navigates to
// them, and the main menu's "Raid" entry keeps its Coming Soon dialog until we
// decide to launch. Reachable by typing /raids so it can be reviewed on a real
// device — a PROD gate would have made that impossible.
const RaidsPage = lazyRoute(() => import("@/pages/Raids"));
const RaidCreatePage = lazyRoute(() => import("@/pages/RaidCreate"));
const RaidLobbyPage = lazyRoute(() => import("@/pages/RaidLobby"));
const RaidBossPage = lazyRoute(() => import("@/pages/RaidBoss"));
const RaidPassportPage = lazyRoute(() => import("@/pages/RaidPassport"));
// PowerScouter is hidden behind ULTIMATE_VISIBLE in SessionProvider — the
// source file stays in the repo so we can re-introduce the live battle
// overlay once the native companion app ships. Its route is TEMPORARILY back
// (2026-08-20) for the main menu's admin-only Review section; ULTIMATE_VISIBLE
// is still false everywhere else.
const PowerScouterPage = lazyRoute(() => import("@/pages/PowerScouter"));
// TEMPORARY review routes (2026-08-20): SubDex lost its registration when
// forms moved onto the species page, and FeaturesMap lost its only consumer
// with PowerScouter. Both are routed again purely so the admin Review section
// in the main menu can open them on-device; delete alongside REVIEW_LINKS.
const SubDexPage = lazyRoute(() => import("@/pages/SubDex"));
const ReviewFeaturesMapPage = lazyRoute(() => import("@/pages/ReviewFeaturesMap"));
import BattleHub from "@/pages/BattleHub";
import Welcome from "@/pages/Welcome";
import SettingsBilling from "@/pages/SettingsBilling";
import Settings from "@/pages/Settings";
import { ScrollToTop } from "@/components/ScrollToTop";
import { V2ThemeProvider } from "@/components/V2ThemeProvider";
import { PageBackground } from "@/components/PageBackground";
import { runDexWipeMigration, scheduleDeferredRemoteWipe } from "@/lib/dexWipeMigration";
import {
  reconcileUnreleasedScanCatches,
  reconcileUntradableLuckyCatches,
  reconcileOrphanedFormCatches,
} from "@/lib/dexStateStore";
import { scheduleDeferredDexResyncUp } from "@/lib/dexResyncUp";
import { startAppHeartbeat } from "@/lib/appHeartbeat";
import { startFirstRunSessions } from "@/lib/firstRunPrompts";
import { gcTombstones } from "@/lib/dexTombstones";

// PR-D3 (2026-06-15): /dex routes directly to the Pokédex-first page.
// The legacy DexView landing and its `?pivot.legacy=on` rollback flag
// were removed once the Pokédex-first scanner had been the only user-
// facing path for two release cycles.

/**
 * Warm the two chunks a trainer opens first (2026-09-30 owner: a cold tap on
 * Dex or My Pokémon showed "Loading..."). Fetched a few seconds after boot,
 * off the critical path, so the first tap finds them already parsed.
 */
function useWarmFirstPages() {
  useEffect(() => {
    const t = setTimeout(() => {
      void importPokedexPage().catch(() => null);
      void importMyPokemonPage().catch(() => null);
    }, 2500);
    return () => clearTimeout(t);
  }, []);
}

function AppRouter() {
  useWarmFirstPages();
  return (
    <Switch>
      {/* Public auth routes (must come before the gate match-all). */}
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/welcome" component={Welcome} />
      <Route path="/settings/billing" component={SettingsBilling} />
      <Route path="/settings" component={Settings} />
      <Route path="/" component={Profile} />
      <Route path="/dex" component={PokedexPage} />
      <Route path="/pokedex" component={PokedexPage} />
      <Route path="/wish-list" component={WishListPage} />
      <Route path="/pokemon/:dexNumber" component={PokemonDetailPage} />
      <Route path="/scan-history" component={ScanHistoryPage} />
      <Route path="/walkthroughs" component={WalkthroughsPage} />
      {/* :id before the bare path, or the list route swallows record links. */}
      <Route path="/my-pokemon/:id" component={MyPokemonDetailPage} />
      <Route path="/my-pokemon" component={MyPokemonPage} />
      <Route path="/stardust-planner" component={StardustPlannerPage} />
      <Route path="/mega" component={MegaEvolutionsPage} />
      <Route path="/naming" component={NamingPage} />
      <Route path="/rocket" component={RocketPage} />
      <Route path="/dynamax" component={DynamaxPage} />
      <Route path="/showcases" component={ShowcasesPage} />
      <Route path="/cleanup" component={BoxCleanupPage} />
      <Route path="/raids/passport" component={RaidPassportPage} />
      <Route path="/raids/host" component={RaidCreatePage} />
      {/* BEFORE /raids/:id, which would otherwise swallow "boss" as a lobby id
          and send the browse-through to a lobby that does not exist. */}
      <Route path="/raids/boss/:bossKey" component={RaidBossPage} />
      <Route path="/raids/:id" component={RaidLobbyPage} />
      <Route path="/raids" component={RaidsPage} />
      {/* Before /friends/:id/* so "manage" is never read as a friend id. */}
      <Route path="/friends/manage" component={FriendsManage} />
      {/* Head-to-head only — the trading that used to share this page now lives
          under /trades. The interim /pokedex route is gone; /compare is the URL
          every existing link and notification already points at. */}
      <Route path="/friends/:id/compare" component={Compare} />
      <Route path="/friends/:id/trades" component={TradeHub} />
      <Route path="/friends" component={Friends} />
      <Route path="/trade" component={Trade} />
      <Route path="/calendar" component={CalendarPage} />
      <Route path="/bonuses" component={BonusesPage} />
      {/* TEMPORARY review routes — see the note on their lazy imports above. */}
      <Route path="/power-scouter" component={PowerScouterPage} />
      <Route path="/dex/:variant/:dexNumber/forms" component={SubDexPage} />
      <Route path="/review/features-map" component={ReviewFeaturesMapPage} />
      <Route path="/battle" component={BattleHub} />
      <Route path="/portal/admin/analytics" component={AdminAnalyticsV2} />
      <Route path="/portal/admin/dataflow" component={AdminDataflow} />
      <Route path="/portal/admin/wiring" component={AdminWiring} />
      <Route path="/portal/admin/email-compose" component={AdminEmailCompose} />
      <Route path="/portal/admin/email-inbox" component={AdminEmailInbox} />
      <Route path="/portal/admin/activity" component={AdminActivity} />
      <Route path="/portal/admin/my-data" component={AdminMyData} />
      <Route path="/portal/admin/captures" component={AdminCaptures} />
      <Route path="/portal/admin/user" component={AdminUser} />
      <Route path="/portal/admin/new-joiners" component={AdminNewJoiners} />
      <Route path="/portal/admin/loyal" component={AdminLoyal} />
      <Route path="/portal/admin/notice-winback" component={AdminNoticeWinback} />
      <Route path="/portal/admin/devices" component={AdminDevices} />
      <Route path="/portal/admin/growth" component={AdminGrowth} />
      <Route path="/portal/admin/acquisition" component={AdminAcquisition} />
      <Route path="/portal/admin/browsing" component={AdminBrowsing} />
      <Route path="/portal/admin/dex" component={AdminDex} />
      <Route path="/portal/admin/dex-backups" component={AdminDexBackups} />
      <Route path="/portal/admin/referrals" component={AdminReferrals} />
      <Route path="/portal/admin/dex-config" component={AdminDexConfig} />
      <Route path="/portal/admin/dex-trading" component={AdminDexTradability} />
      <Route path="/portal/admin/events" component={AdminEvents} />
      <Route path="/portal/admin/beta-signups" component={AdminBetaSignups} />
      <Route path="/portal/admin/play-notify" component={AdminPlayNotify} />
      <Route path="/portal/admin/deletions" component={AdminDeletions} />
      <Route path="/portal/admin/raid-reports" component={AdminRaidReports} />
      <Route path="/portal/admin/system" component={AdminSystem} />
      <Route path="/portal/admin/archive" component={AdminArchive} />
      {/* Registered after the /portal/admin/* pages, matching the /portal
          convention below: least-specific admin path goes last. */}
      <Route path="/portal/admin" component={AdminHome} />
      <Route path="/admin/activity" component={AdminActivity} />
      <Route path="/portal/auth/verify-email" component={AuthVerifyEmail} />
      <Route path="/auth/verify-email" component={AuthVerifyEmail} />
      <Route path="/portal/auth/forgot-password" component={AuthForgotPassword} />
      <Route path="/auth/forgot-password" component={AuthForgotPassword} />
      <Route path="/portal/auth/reset-password" component={AuthResetPassword} />
      <Route path="/auth/reset-password" component={AuthResetPassword} />
      <Route path="/portal/dex" component={PortalDex} />
      <Route path="/portal/upload" component={PortalUpload} />
      <Route path="/portal/account" component={PortalAccount} />
      <Route path="/portal" component={PortalHome} />
      <Route component={NotFound} />
    </Switch>
  );
}

/**
 * Build 6 / v1.4.6 — dismiss the pre-React boot layers AFTER React mounts.
 * Build 8 / v1.4.6 — add double-rAF paint gate + 800ms minimum dwell.
 *
 * Build 6 moved dismiss into `useEffect`, tying it to React's first render
 * COMMIT. But on a cold WebView the bundle parse, Capacitor bridge handshake,
 * and first rasterisation all happen AFTER React commits — the device log
 * showed `[splash] dismiss {elapsed_ms:58}` while the user saw 10-15s of
 * black screen (iOS launch image) because the WebView hadn't painted yet.
 *
 * Build 8 fix: dismiss only after BOTH:
 *   1. React useEffect fired (current condition, guarantees tree is mounted)
 *   2. Double requestAnimationFrame (guarantees at least one paint cycle)
 *   3. 800ms minimum dwell — covers slow-paint devices AND gives the eye a
 *      beat to register the branded splash before handing off.
 *
 * The 800ms floor is measured from the JS module load timestamp (__bootAt__),
 * not from the useEffect, so slow React trees don't add a FURTHER 800ms on top
 * of an already-slow boot — only if the total elapsed time since module load
 * is still under 800ms do we hold.
 */
const __bootAt__ = Date.now();

/**
 * b13d S8 — warm the dex data + the lazy Pokédex chunk DURING the splash
 * dwell so the dex renders from cache the instant the splash dismisses,
 * instead of showing its own loading state.
 *
 * Fire-and-forget: prefetch failures (e.g. a 401 before auth resolves) are
 * swallowed by React Query and never block the boot. The queries use the same
 * keys the dex surfaces read, so a successful warm-up populates their cache.
 */
function prefetchDexData(): void {
  const dexKeys = [
    ["/api/dex/completion"],
    ["/api/dex/batches"],
    ["/api/dex/records"],
  ];
  for (const queryKey of dexKeys) {
    void queryClient.prefetchQuery({ queryKey }).catch(() => null);
  }
  // Warm the code-split Pokédex page chunk so navigation doesn't pay the
  // dynamic-import cost after the splash.
  void import("@/pages/Pokedex").catch(() => null);
}

async function dismissBootLayers(): Promise<void> {
  // 0. Sprite warm (2026-10-07): start loading the bundled art behind the
  //    splash, and hold the splash for the first screen's cells, capped so a
  //    slow bridge can never pin the trainer on the logo. The rest carries on
  //    in the background. See lib/spriteWarm.
  const warm = startSpriteWarm();
  await Promise.race([warm.firstScreen, new Promise<void>((r) => setTimeout(r, SPRITE_WARM_SPLASH_CAP_MS))]);

  // 1. Double rAF — guarantees one paint cycle has completed.
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

  // 2. 800ms minimum dwell from boot (not from useEffect).
  const remaining = 800 - (Date.now() - __bootAt__);
  if (remaining > 0) {
    await new Promise<void>((r) => setTimeout(r, remaining));
  }

  // eslint-disable-next-line no-console
  console.log("[splash] dismiss", {
    elapsed_ms: Date.now() - __bootAt__,
    paint_gate: true,
    min_dwell_satisfied: true,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__hideSplash?.();

  // b13b.2: the @capacitor/splash-screen plugin was removed on iOS. The native
  // LaunchScreen.storyboard is now the only OS-level splash, and AppDelegate
  // keeps the WKWebView hidden (alpha 0) until its first paint, then fades it
  // in over 0.25s — so the storyboard never races a JS-driven SplashScreen.hide()
  // and there is no black frame between them. Inside the WebView the #nexus-boot
  // CSS splash above covers the bundle-parse gap, dismissed via __hideSplash().
  // Nothing more to do here on native: there is no SplashScreen.hide() to call.
}

function App() {
  // b13c.7 — run the one-time clean-slate dex wipe BEFORE the paint gate so the
  // empty dex is in place before any page reads it. Idempotent: it self-stamps
  // `lastWipedAppVersion` and never runs again once on 1.4.7+.
  useEffect(() => {
    try {
      runDexWipeMigration();
    } catch {
      /* a wipe failure must never block launch */
    }
    // PR V: garbage-collect reconciliation tombstones older than the TTL on
    // launch so the blob stays bounded. A stale tombstone whose target server
    // row never arrived is no longer doing any work. Best-effort: never block
    // launch.
    try {
      gcTombstones();
    } catch {
      /* GC failure is harmless — tombstones simply persist until next launch */
    }
    // Scanner integrity: retract scan-written catches for species not released in
    // GO (the 2026-07-26 Galar false-writes that inflated Main above the roster).
    // The write guard blocks new ones; this cleans any already on the device and
    // syncs the correction up. Idempotent + scan-source-only, so it never touches
    // a manual catch. Best-effort: a failure must never block launch.
    try {
      reconcileUnreleasedScanCatches();
    } catch {
      /* reconciliation failure is harmless — the write guard still holds */
    }
    // Trade-gate integrity: retract Lucky / Shiny-Lucky catches for species the
    // GO Game Master says cannot be traded (the 2026-08-07 hand-tapped Lucky
    // Keldeo #647, which counted on the National ring but not on GO-Released).
    // Unlike the scan reconcile above this may clear a MANUAL row — safe because
    // its input is the game's own isTradable flag, not our released roster. Also
    // best-effort: never block launch.
    try {
      reconcileUntradableLuckyCatches();
    } catch {
      /* reconciliation failure is harmless — the write guard still holds */
    }
    // Catalogue integrity: retract catches on forms the app has stopped
    // modelling. 2026-08-19 removed 61 forms Pokémon GO never grants (Darmanitan
    // Zen, Aegislash Blade, the Arceus plates …); the rows survived and, because
    // /compare reads dex_records rather than the form catalogue, each one became
    // its own unlabelled trade lead for a copy that cannot exist. Touches only
    // rows carrying a formId, so the species catch underneath is never lost.
    // Best-effort like its siblings: never block launch.
    try {
      reconcileOrphanedFormCatches();
    } catch {
      /* reconciliation failure is harmless — the grid reads the catalogue */
    }
    // The clean-slate server wipe is RETIRED (it deleted real synced dexes
    // post-launch — see dexWipeMigration.ts). This call now only DEFUSES any
    // stale pending-wipe marker a prior build left queued, so no device can fire
    // the destructive DELETE /api/dex/state again. It never contacts the server.
    scheduleDeferredRemoteWipe(apiRequestRaw, hasAuthToken);
    // Restore: re-upload the surviving LOCAL dex to the server once per device,
    // repairing accounts whose server dex_records were wiped by the retired
    // audit-backfill DELETE while the device kept its full local copy. Additive
    // (upsert-only), gated on a localStorage version marker + auth, post-launch.
    scheduleDeferredDexResyncUp(apiRequestRaw, hasAuthToken);
    // Retention: record that the app was opened, now and on every return to the
    // foreground. Nothing recorded this before — the admin dashboard's session
    // count is sign-ins on a 30-day token, so a daily user and a one-time
    // opener were the same number. Throttled to 1/15min, silent on failure.
    startAppHeartbeat(apiRequestRaw, hasAuthToken);
    // The optional first-run asks (where from, feedback, review, Pro sheet,
    // the notification sheet) are rationed to one per session, and this is
    // what counts a session: a launch, or a return after half an hour away.
    startFirstRunSessions();
    // b13d S8: kick off dex prefetch in parallel with the splash min-dwell.
    prefetchDexData();
    // The on-device colour-sprite precache/download was removed: every base +
    // shiny (1..1025) is now shipped in the binary (client/public/sprites-bundled,
    // see CaughtSprite + pokemonArtwork.bundledArtworkUrl), so re-downloading
    // full-res copies was redundant churn — and its live source-swap made cells
    // flash blank while a download settled. Bundled art is the on-device source;
    // strays (forms, >1025) fall through to the CDN.
    void dismissBootLayers();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <SessionProvider>
            {/* Keeps the APNs token registered on launch and makes foreground
                pushes visible + tappable. See PushLifecycle for the why. */}
            <PushLifecycle />
            {/* A fresh install's Profile read all zeros until the Dex page ran
                its pull; this pulls the moment a Pro trainer is signed in. */}
            <DexSyncOnSignIn />
            <PrioritiesSyncer />
            {/* wish-list-screen: mirror the local Wish List to the server so
                friends can read it on the Compare page. */}
            <WishlistSyncer />
            <MyPokemonSyncer />
            {/* Auto Scan on open (Settings > Scanning): sweeps the Screenshots
                album quietly on launch and on every return to the foreground. */}
            <AutoScanOnOpen />
            {/* Reports which dex tabs this device hides. Push-only — see the
                component; visibility stays a per-device choice. */}
            <DexVisibilitySyncer />
            {/* Roster automation: load the live Shadow/Purified roster from
                the Worker once and union it onto the bundled baseline floor. */}
            <ShadowRosterSyncer />
            <DexConfigSyncer />
            {/* Admin-edited copy: replayed from cache, then fetched (public GET). */}
            <CopyOverridesSyncer />
            <InventoryProvider>
              <DexSpriteProvider>
              <BattleProvider>
                {/* v1.1.1-b4: BroadcastProvider owns the live-scan state
                    machine + frame upload pump as a singleton. The pill
                    in Inventory.tsx and the banner in DexView.tsx both
                    read from this one hook instance. */}
                <BroadcastProvider>
                {/* 2026-07: LiveScanProvider runs useDexLiveScan ONCE, above the
                    per-page <AppShell>, so the live scan session (ReplayKit
                    broadcast, frame poller, PiP) survives navigation instead of
                    being torn down + re-firing the iOS picker on every route
                    change. Must wrap <Router> so HeroDexButton + DexLiveScanMenu
                    (rendered inside AppRouter) read the one shared session. */}
                <LiveScanProvider>
                <Toaster />
                {/* Global: a running Poké Genie import shows its x/y progress
                    on every page, not just the sheet that started it. */}
                <MyPokemonImportBar />
                {/* Auto Scan album sweep: same global-bar pattern, same slot. */}
                <AutoScanBar />
                {/* "Relevant Pokédex entries have been updated" + Undo. Global
                    because the detail page commits its dex ticks as it LEAVES. */}
                <DexLinkUndoBar />
                <GlobalErrorReporter />
                <GlobalKeyboard />
                <Router hook={useHashLocation}>
                  <V2ThemeProvider />
                  <PageBackground variant="app" />
                  <ScrollToTop />
                  {/* Admin only: the in-app copy editor (main menu > Edit copy). */}
                  <CopyEditMode />
                  {/* Inside <Router> so it sees every navigation. */}
                  <PageViewReporter />
                  {/* The only attribution an iOS store install will ever have. */}
                  <HeardAboutPrompt />
                  <FeatureIdeaPrompt />
                  {/* The one-time set-up of the automatic event countdowns (2026-10-30). */}
                  <LiveEventsIntroSheet />
                  {/* The store rating sheet at a collection milestone (headless;
                      the scan sheet fires the other moment itself). */}
                  <RatingMoments />
                  <SearchMovedNotice />
                  <WhyProSheet />
                  {/* Floating "N bonuses live" pill (lower-right). Mounted here,
                      not in AppShell, so it survives route changes. */}
                  <ActiveBonusesOverlay />
                  {/* Dex Live Scan menu, route-independent so a Live Scan tap
                      from any screen opens it at once (2026-09-23). */}
                  <GlobalDexLiveScanMenu />
                  {/* The running scan as a floating orb once the sheet has
                      dismissed itself (2026-09-28); a tap re-opens the sheet. */}
                  <LiveScanFloatingOrb />
                  {/* Android HUD card buttons -> app actions (2026-09-29). */}
                  <LiveScanHudActions />
                  <RequireAuth>
                    <Suspense
                      fallback={
                        <div className="min-h-[40vh] flex items-center justify-center text-xs text-muted-foreground">
                          Loading...
                        </div>
                      }
                    >
                      <AppRouter />
                    </Suspense>
                  </RequireAuth>
                  {/*
                    AuthModal + ProfileGate must live INSIDE <Router> because
                    AuthModal calls useLocation() to redirect after profile
                    completion. Mounting them outside Router throws
                    "Could not find an enclosing <Router>" and crashes the
                    whole app to a black screen on launch.
                  */}
                  <DeepLinkHandler />
                  <TradeExecutionFlow />
                  <AuthModal />
                  <ProfileGate />
                  <FeedbackButton />
                  {/* Mounted once here (not in the per-page AppShell) so the
                      walkthrough's step index survives the route changes it
                      drives. It spotlights elements by data-testid via a global
                      DOM query, so it does not matter that it renders outside
                      any given page's shell. */}
                  <GuidedTour />
                </Router>
                </LiveScanProvider>
                </BroadcastProvider>
              </BattleProvider>
              </DexSpriteProvider>
            </InventoryProvider>
          </SessionProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
